import React, {useEffect, useState} from "react"
// mui
import {
    Box, Button, Collapse, FormControl, Grid2 as Grid, InputLabel, MenuItem, Select,
    Stack, TextField, Typography,
} from "@mui/material";
import {Download, InfoOutlined} from "@mui/icons-material";
// translation
import {useTranslation} from "react-i18next";
// data
import {useData} from "@/context/PackagingDataProvider";
import BrandRangeSelect from "@/components/UI/BrandRangeSelect/BrandRangeSelect";
import {useModule} from "@/context/ModuleProvider";
import {PageHeader, SectionCard} from "@/components/Layout";
import {packagingTokens as tk} from "@/theme/packagingTokens";

const MIN_DIMX = 0.395
const MAX_DIMX = 1

export default function DownloadTab() {

    const {t} = useTranslation(["download", "products", "common"])
    const {modules} = useModule()
    const {
        brands, ranges, getBrands, getRanges,
        downloadQRCodesBatch,
        downloadableProducts, totalDownloadableProducts, getDownloadableProducts,
        batchesIds, getBatchesIDs,
    } = useData()

    const [filterType, setFilterType] = useState<"default" | "batch-id" | "all">("default")
    const [displayedRanges, setDisplayedRanges] = useState<any>([])
    const [generating, setGenerating] = useState<boolean>(false)
    const [filters, setFilters] = useState<any>({brand: "", range: "", type: "", batchId: "", archived: "false"})
    const [file, setFile] = useState<any>({format: "png", unit: "px", size: "300", resolution: "75", real_size: false, dim_x: 0.420})

    useEffect(() => {
        if (!brands) getBrands()
        if (!ranges) getRanges()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setFilters((f: any) => ({...f, range: ""}))
        if (!!filters.brand) setDisplayedRanges(ranges.filter((r: any) => r.brand.id === filters.brand))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.brand])

    useEffect(() => {
        // v3 rewrite: the getDownloadableProducts calls below already send the
        // snake_case max_rows the API reads (dev's #209 patched the old
        // `submitted` effect, which no longer exists here).
        if (modules.gs1) setFilters((f: any) => ({...f, type: "GS1"}))
        else setFilters((f: any) => ({...f, type: "Standard"}))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modules]);

    useEffect(() => {
        if (file.dim_x > MAX_DIMX) setFile((f: any) => ({...f, dim_x: MAX_DIMX}))
        else if (file.dim_x < MIN_DIMX) setFile((f: any) => ({...f, dim_x: MIN_DIMX}))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file.dim_x]);

    useEffect(() => {
        if (filters.type === "GS1") setFile((f: any) => ({...f, format: "svg", real_size: true}))
        else setFile((f: any) => ({...f, format: "png", real_size: false}))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.type]);

    useEffect(() => {
        if (filterType === "batch-id") getBatchesIDs()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType])

    // Fetch the matching product count whenever a downloadable scope is set:
    // brand+range in "default" mode, or just a product type in "all" mode
    // (the products endpoint treats empty brand/range as "no filter").
    useEffect(() => {
        if (filterType === "default" && filters.brand && filters.range) {
            getDownloadableProducts({brand: filters.brand, range: filters.range, type: filters.type, max_rows: "9999", page: "1", org: "", search: ""})
        } else if (filterType === "all" && filters.type) {
            getDownloadableProducts({brand: "", range: "", type: filters.type, max_rows: "9999", page: "1", org: "", search: ""})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.brand, filters.range, filters.type, filterType])

    const isGS1 = filters.type === "GS1"

    const isCorrectlySized =
        (file.unit === "px" && (Number(file.size) >= 100 && Number(file.size) <= 2500)) ||
        (file.unit === "mm" && file.size >= 30 && (
            (file.resolution === "75" && Number(file.size) <= 847) || (file.resolution === "300" && Number(file.size) <= 212)
        ))

    // Scope readiness per filter mode: brand+range ("default"), a batch id
    // ("batch-id"), or a product type ("all" — GS1 and Standard have different
    // output requirements, so a mixed all-types download is not allowed).
    const scopeReady =
        filterType === "default" ? !!filters.brand && !!filters.range && !!downloadableProducts?.length :
        filterType === "all" ? !!filters.type && !!downloadableProducts?.length :
        !!filters.batchId

    const canGenerate = isGS1 ? scopeReady : isCorrectlySized && scopeReady

    const generate = async () => {
        setGenerating(true)
        try {
            if (filterType === "batch-id") {
                await downloadQRCodesBatch(filters.batchId, file, "batch")
            } else {
                // "all" downloads every product of the selected type (no brand/range scope)
                const scope = filterType === "all" ? {brand: "", range: ""} : {brand: filters.brand, range: filters.range}
                let uuids = downloadableProducts
                if (!uuids?.length) {
                    uuids = await getDownloadableProducts({...scope, type: filters.type, max_rows: "9999", page: "1", org: "", search: ""})
                }
                if (uuids?.length) await downloadQRCodesBatch(uuids, file, "files")
            }
        } finally {
            setGenerating(false)
        }
    }

    // ── Format cards (PNG / JPEG / SVG / PDF) ──
    const FORMATS = [
        {value: "png", name: "PNG", desc: t("format_desc_png", {defaultValue: "Raster · Web"})},
        {value: "jpeg", name: "JPEG", desc: t("format_desc_jpeg", {defaultValue: "Raster · Photo"})},
        {value: "svg", name: "SVG", desc: t("format_desc_svg", {defaultValue: "Vector · Print"})},
        {value: "pdf", name: "PDF", desc: t("format_desc_pdf", {defaultValue: "Document"})},
    ]

    const renderConfigureBox = () => (
        <SectionCard title={t("download_chose_filters", {defaultValue: "Configure Download"})}>
            <Typography sx={{fontSize: "13px", color: tk.color.textSecondary, mb: 2}}>
                {t("download_intro")}
            </Typography>

            <Stack spacing={2}>
                <FormControl fullWidth size={"small"}>
                    <InputLabel id={"filter-selector"}>{t("products:filters_type")}</InputLabel>
                    <Select label={t("products:filters_type")} labelId={"filter-selector"} value={filterType}
                            onChange={(e: any) => setFilterType(e.target.value)}>
                        <MenuItem value={"default"}>{t("products:filter_default")}</MenuItem>
                        <MenuItem value={"batch-id"}>{t("products:filter_batch-id")}</MenuItem>
                        <MenuItem value={"all"}>{t("products:filter_all")}</MenuItem>
                    </Select>
                </FormControl>

                <Collapse in={filterType === "default"}>
                    <Stack spacing={2}>
                        <BrandRangeSelect small label={t("common:brand")} type={"brand"} data={brands} value={filters.brand}
                                          setValue={(e: any) => setFilters({...filters, brand: e.target.value})} error={!filters.brand} {...{t}} />
                        <Collapse in={!!filters.brand}>
                            <BrandRangeSelect small label={t("common:range")} type={"range"} data={displayedRanges} value={filters.range}
                                              setValue={(e: any) => setFilters({...filters, range: e.target.value})} error={!filters.range} disabled={!filters.brand} {...{t}} />
                        </Collapse>
                        {(!!modules && modules.gs1 && modules.geoloc) && (
                            <FormControl fullWidth size={"small"}>
                                <InputLabel id={"type-selector"}>{t("products:type")}</InputLabel>
                                <Select label={t("products:type")} labelId={"type-selector"} value={filters.type || ""}
                                        onChange={(e: any) => setFilters({...filters, type: e.target.value})}>
                                    <MenuItem value={""}>{t("products:no_filter")}</MenuItem>
                                    <MenuItem value={"Standard"}>{t("products:geolocation")}</MenuItem>
                                    <MenuItem value={"GS1"}>{t("products:gs1")}</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        {!!filters.brand && !!filters.range && (
                            <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                                {t("you_have_selected")}{" "}
                                <Box component={"strong"} sx={{color: tk.color.greenDark}}>{totalDownloadableProducts ?? 0}</Box>{" "}
                                {t("products:products").toLowerCase()}
                            </Typography>
                        )}
                    </Stack>
                </Collapse>

                {/* "All" mode: no brand/range scoping — only the product type,
                    which stays mandatory (GS1 and Standard QR codes don't share
                    the same visual/output requirements, so no mixed download). */}
                <Collapse in={filterType === "all"}>
                    <Stack spacing={2}>
                        {(!!modules && modules.gs1 && modules.geoloc) && (
                            <FormControl fullWidth size={"small"}>
                                <InputLabel id={"all-type-selector"}>{t("products:type")}</InputLabel>
                                <Select label={t("products:type")} labelId={"all-type-selector"} value={filters.type || ""}
                                        error={!filters.type}
                                        onChange={(e: any) => setFilters({...filters, type: e.target.value})}>
                                    <MenuItem value={"Standard"}>{t("products:geolocation")}</MenuItem>
                                    <MenuItem value={"GS1"}>{t("products:gs1")}</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        {!!filters.type && (
                            <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                                {t("you_have_selected")}{" "}
                                <Box component={"strong"} sx={{color: tk.color.greenDark}}>{totalDownloadableProducts ?? 0}</Box>{" "}
                                {t("products:products").toLowerCase()}
                            </Typography>
                        )}
                    </Stack>
                </Collapse>

                <Collapse in={filterType === "batch-id"}>
                    <FormControl fullWidth size={"small"}>
                        <InputLabel id={"batch-id-selector"}>{t("products:batchid")}</InputLabel>
                        <Select label={t("products:batchid")} labelId={"batch-id-selector"} value={filters.batchId || ""}
                                onChange={(e: any) => setFilters({...filters, batchId: e.target.value})} disabled={!batchesIds}>
                            <MenuItem value={""} disabled>{t("products:no_filter")}</MenuItem>
                            {!!batchesIds && batchesIds.map((batch: any, i: number) => (
                                <MenuItem value={batch.batch_id} key={i}>{batch.batch_id}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Collapse>

                <Button fullWidth variant={"contained"} disableElevation size={"large"} startIcon={<Download />}
                        disabled={!canGenerate || generating} onClick={generate}
                        sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm}}>
                    {generating ? t("download_processing") : t("download_my_qrcodes")}
                </Button>
            </Stack>
        </SectionCard>
    )

    const renderFormatBox = () => (
        <SectionCard title={t("file_size_and_format", {defaultValue: "Export Format"})}>
            {isGS1 ? (
                <Stack spacing={2}>
                    <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                        {t("products:gs1_download_intro", {defaultValue: "Print-ready, real-size, GS1-certified SVG files."})}
                    </Typography>
                    {/* GS1 codes are certified vector output: SVG, or a PDF
                        carrying the same vector content (pdf_content=vector). */}
                    <FormControl fullWidth size={"small"}>
                        <InputLabel id={"gs1-file-format"}>{t("file_format")}</InputLabel>
                        <Select label={t("file_format")} labelId={"gs1-file-format"} value={file.format}
                                onChange={(e) => {
                                    const format = e.target.value
                                    setFile({...file, format, pdf_content: format === "pdf" ? "vector" : undefined})
                                }}>
                            <MenuItem value={"svg"}>SVG</MenuItem>
                            <MenuItem value={"pdf"}>{t("gs1_pdf_option")}</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField size={"small"} fullWidth type={"number"} label={t("dimX")} value={file.dim_x}
                               onChange={(e) => setFile({...file, dim_x: e.target.value})}
                               inputProps={{min: MIN_DIMX, max: MAX_DIMX, step: 0.005}} />
                </Stack>
            ) : (
                <Stack spacing={2.5}>
                    {/* Format cards */}
                    <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 1}}>
                        {FORMATS.map((f) => {
                            const selected = file.format === f.value
                            return (
                                <Box key={f.value} onClick={() => setFile({...file, format: f.value})}
                                     sx={(theme) => ({
                                         cursor: "pointer", textAlign: "center", px: 1, py: 1.25,
                                         borderRadius: tk.radius.card,
                                         border: `1px solid ${selected ? theme.palette.primary.main : tk.color.border}`,
                                         backgroundColor: selected ? `${theme.palette.primary.main}14` : tk.color.surface,
                                         transition: "all .15s",
                                         "&:hover": {borderColor: theme.palette.primary.main},
                                     })}>
                                    <Typography sx={(theme) => ({fontSize: "13.5px", fontWeight: 700, color: selected ? theme.palette.primary.main : tk.color.textPrimary})}>
                                        {f.name}
                                    </Typography>
                                    <Typography sx={{fontSize: "11px", color: tk.color.textTertiary}}>{f.desc}</Typography>
                                </Box>
                            )
                        })}
                    </Box>

                    {/* What the PDF embeds per QR: raster PNG (default) or the
                        generator's true vector page (print quality). */}
                    <Collapse in={file.format === "pdf"}>
                        <FormControl fullWidth size={"small"}>
                            <InputLabel id={"pdf-content"}>{t("pdf_content")}</InputLabel>
                            <Select label={t("pdf_content")} labelId={"pdf-content"} value={file.pdf_content ?? "raster"}
                                    onChange={(e) => setFile({...file, pdf_content: e.target.value})}>
                                <MenuItem value={"raster"}>{t("pdf_content_raster")}</MenuItem>
                                <MenuItem value={"vector"}>{t("pdf_content_vector")}</MenuItem>
                            </Select>
                        </FormControl>
                    </Collapse>

                    <FormControl fullWidth size={"small"}>
                        <InputLabel id={"file-unit"}>{t("file_unit")}</InputLabel>
                        <Select label={t("file_unit")} labelId={"file-unit"} value={file.unit} onChange={(e) => setFile({...file, unit: e.target.value})}>
                            <MenuItem value={"px"}>{t("unit_pixels")}</MenuItem>
                            <MenuItem value={"mm"}>{t("unit_millimeters")}</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField label={t("file_size")} value={file.size} onChange={(e) => setFile({...file, size: e.target.value})}
                               fullWidth size={"small"} type={"number"} error={!isCorrectlySized}
                               InputProps={{
                                   endAdornment: <Typography variant={"body2"}>{file.unit}</Typography>,
                                   inputProps: {max: file.unit === "px" ? 2500 : (file.unit === "mm" && file.resolution === "300") ? 212 : 847, min: file.unit === "px" ? 100 : 30},
                               }}
                               helperText={file.unit === "px" ? t("file_size_alert_px") : (file.unit === "mm" && file.resolution === "75") ? t("file_size_alert_75") : t("file_size_alert_300")} />

                    <Collapse in={file.unit === "mm"}>
                        <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
                            <Typography sx={{fontSize: "13px"}}>{t("resolution")}</Typography>
                            {["75", "300"].map((r) => (
                                <Button key={r} size={"small"} disableElevation variant={file.resolution === r ? "contained" : "outlined"}
                                        onClick={() => setFile({...file, resolution: r})} sx={{textTransform: "none", borderRadius: tk.radius.sm}}>
                                    {r} ppi
                                </Button>
                            ))}
                        </Stack>
                    </Collapse>
                </Stack>
            )}

            {/* Naming convention info box */}
            <Box sx={{mt: 2.5, px: 2, py: 1.5, borderRadius: tk.radius.card, backgroundColor: tk.color.green50, border: `1px solid ${tk.color.green100}`}}>
                <Stack direction={"row"} spacing={1} alignItems={"center"} sx={{mb: 0.5}}>
                    <InfoOutlined sx={{fontSize: 15, color: tk.color.greenDark}} />
                    <Typography sx={{fontSize: "13px", fontWeight: 600, color: tk.color.greenDark}}>
                        {t("naming_convention", {defaultValue: "Naming convention"})}
                    </Typography>
                </Stack>
                <Typography sx={{fontSize: "12px", color: tk.color.textSecondary}}>
                    {t("naming_convention_hint", {defaultValue: "Files are bundled into a ZIP, named after each product."})}
                </Typography>
            </Box>
        </SectionCard>
    )

    return (
        <Box>
            <PageHeader title={t("download_qrcodes", {defaultValue: "Batch Download"})}
                        subtitle={t("download_subtitle", {defaultValue: "Download QR codes associated to your products in bulk"})} />
            <Grid container spacing={2.5} alignItems={"flex-start"}>
                <Grid size={{xs: 12, md: 6}}>{renderConfigureBox()}</Grid>
                <Grid size={{xs: 12, md: 6}}>{renderFormatBox()}</Grid>
            </Grid>
        </Box>
    )
}
